// Packet-capture widget (Wireshark-lite). Listens to the lab runtime while a
// capture is running and synthesises the frames that each command actually
// produced, based on its real simulated result (success, timeout, refused).

import { h } from './h.js';
import { macColon } from '../engine/net/addr.js';
import { hostIp } from '../engine/net/sim.js';

const subs = new Map();

// `s` is the widget's data: the ephemeral-port / DNS-id counter lives there (not in a module
// variable) so the same commands always produce the same packets — in the browser and when the
// server replays the lab (src/core/labVerify.js).
function packetsFor(ev, rt, dev, s) {
  const d = rt.net.devices[dev];
  const me = hostIp(d)?.ip || '0.0.0.0';
  const myMac = macColon(d.nic.mac);
  const gwMac = (ip) => (d.arp[ip]?.mac ? macColon(d.arp[ip].mac) : 'ff:ff:ff:ff:ff:ff');
  const out = [];
  const eph = () => 49152 + ((s.seq = (s.seq || 0) + 97) % 16000);
  const dnsServer = hostIp(d)?.dns?.[0];
  const nextHopMac = (dst) => gwMac(hostIp(d)?.gw) || gwMac(dst);
  const P = (o) => out.push({ src: me, dst: o.dst, smac: myMac, dmac: nextHopMac(o.dst), ttl: 128, ...o });
  const R = (o) => out.push({ src: o.from, dst: me, smac: nextHopMac(o.from), dmac: myMac, ttl: 62, ...o });
  const dns = (name, ok) => {
    if (!dnsServer) return;
    const sp = eph(), id = (0x1a2b + s.seq) & 0xffff;
    P({ proto: 'DNS', l4: 'UDP', sport: sp, dport: 53, dst: dnsServer, len: 74, info: `Standard query 0x${id.toString(16)} A ${name}` });
    if (ok === true) R({ proto: 'DNS', l4: 'UDP', from: dnsServer, sport: 53, dport: sp, len: 90, info: `Standard query response 0x${id.toString(16)} A ${name} A ${ok === true ? ev.result?.address || ev.args?.ip || '' : ''}`.trim() });
    else if (ok === 'timeout') { P({ proto: 'DNS', l4: 'UDP', sport: sp, dport: 53, dst: dnsServer, len: 74, info: `Standard query 0x${id.toString(16)} A ${name} (retransmission)` }); }
  };
  const tcp = (ip, port, result) => {
    const sp = eph();
    const flags = (f, o = {}) => ({ proto: 'TCP', l4: 'TCP', sport: sp, dport: port, dst: ip, flags: f, len: 66, info: `${sp} → ${port} [${f}] Seq=${o.seq ?? 0}${o.ack != null ? ` Ack=${o.ack}` : ''} Win=64240`, seq: o.seq ?? 0, ack: o.ack });
    const back = (f, o = {}) => ({ proto: 'TCP', l4: 'TCP', from: ip, sport: port, dport: sp, flags: f, len: 66, info: `${port} → ${sp} [${f}] Seq=${o.seq ?? 0}${o.ack != null ? ` Ack=${o.ack}` : ''} Win=65535`, seq: o.seq ?? 0, ack: o.ack });
    P(flags('SYN'));
    if (result === 'ok') {
      R(back('SYN, ACK', { seq: 0, ack: 1 }));
      P(flags('ACK', { seq: 1, ack: 1 }));
      P({ ...flags('PSH, ACK', { seq: 1, ack: 1 }), proto: port === 443 ? 'TLSv1.3' : 'HTTP', len: 140, info: port === 443 ? 'Client Hello' : `GET / HTTP/1.1  Host: ${ev.args?.host}` });
      R({ ...back('PSH, ACK', { seq: 1, ack: 75 }), proto: port === 443 ? 'TLSv1.3' : 'HTTP', len: 1320, info: port === 443 ? 'Server Hello, Certificate, Finished' : 'HTTP/1.1 200 OK  (text/html)' });
      P(flags('ACK', { seq: 75, ack: 1255 }));
      P(flags('FIN, ACK', { seq: 75, ack: 1255 }));
      R(back('FIN, ACK', { seq: 1255, ack: 76 }));
      P(flags('ACK', { seq: 76, ack: 1256 }));
    } else if (result === 'refused') R(back('RST, ACK', { seq: 1, ack: 1 }));
    else { P({ ...flags('SYN'), info: `[TCP Retransmission] ${sp} → ${port} [SYN] Seq=0` }); P({ ...flags('SYN'), info: `[TCP Retransmission] ${sp} → ${port} [SYN] Seq=0` }); }
  };
  if (ev.type !== 'cmd' || ev.device !== dev) return out;
  const k = ev.key;
  if (k === 'nslookup') dns(ev.args?.name, ev.result?.success ? true : 'timeout');
  if (k === 'ping' && !ev.args?.v6) {
    if (ev.args?.target && ev.args.target !== ev.args.ip) dns(ev.args.target, ev.args.ip ? true : 'timeout');
    if (ev.args?.ip) {
      const n = 4;
      for (let i = 0; i < n; i++) {
        P({ proto: 'ICMP', l4: 'ICMP', dst: ev.args.ip, len: 74, info: `Echo (ping) request  id=0x0001, seq=${i + 1}/${(i + 1) * 256}, ttl=128` });
        if (ev.result?.success) R({ proto: 'ICMP', l4: 'ICMP', from: ev.args.ip, len: 74, info: `Echo (ping) reply    id=0x0001, seq=${i + 1}/${(i + 1) * 256}` });
      }
    }
  }
  if (k === 'curl' || k === 'Test-NetConnection') {
    const host = ev.args?.host;
    const port = ev.args?.port || 80;
    const byName = host && !/^\d+\.\d+\.\d+\.\d+$/.test(host);
    const ok = ev.result?.success;
    const ip = ev.args?.ip || (byName ? null : host);
    if (byName) dns(host, ip && ev.result?.dns !== false ? true : 'timeout');
    if (ip && ev.result?.dns !== false) tcp(ip, port, ok ? 'ok' : ev.result?.refused ? 'refused' : 'timeout');
  }
  return out;
}

function detail(p) {
  const l4 = p.l4 === 'TCP'
    ? `Transmission Control Protocol, Src Port: ${p.sport}, Dst Port: ${p.dport}\n    Flags: [${p.flags}]\n    Sequence number: ${p.seq}${p.ack != null ? `\n    Acknowledgment number: ${p.ack}` : ''}\n    Window: ${p.src.startsWith('192.') || p.src.startsWith('10.') ? 64240 : 65535}`
    : p.l4 === 'UDP' ? `User Datagram Protocol, Src Port: ${p.sport}, Dst Port: ${p.dport}\n    Length: ${p.len - 34}\n    Checksum: 0x${(p.len * 977 % 65535).toString(16)} [unverified]`
      : 'Internet Control Message Protocol';
  const app = p.proto === 'DNS' ? `\nDomain Name System (${p.sport === 53 ? 'response' : 'query'})\n    ${p.info}` : p.proto === 'HTTP' ? `\nHypertext Transfer Protocol\n    ${p.info}` : p.proto.startsWith('TLS') ? `\nTransport Layer Security\n    ${p.info}` : '';
  return `Frame ${p.no}: ${p.len} bytes on wire\nEthernet II, Src: ${p.smac}, Dst: ${p.dmac}\n    Type: IPv4 (0x0800)\nInternet Protocol Version 4, Src: ${p.src}, Dst: ${p.dst}\n    Time to Live: ${p.ttl}\n    Protocol: ${p.l4 === 'ICMP' ? 'ICMP (1)' : p.l4 === 'TCP' ? 'TCP (6)' : 'UDP (17)'}\n${l4}${app}`;
}

/**
 * The capture itself, without any DOM: start / stop / clear arrive as learner actions, and while
 * running every command on `dev` adds the frames it produced. Used by the widget and, headless,
 * by the server replay. Returns the unsubscribe function.
 */
function attachCapture(rt, id, dev, { done, changed } = {}) {
  return rt.on((type, ev) => {
    if (type !== 'event') return;
    const s = rt.widget(id).data;
    s.packets = s.packets || [];
    if (ev.type === 'action' && ev.widget === id && /^capture-(start|stop|clear)$/.test(ev.name)) {
      if (ev.name === 'capture-start') s.running = true;
      else if (ev.name === 'capture-stop') s.running = false;
      else s.packets = [];
      rt.save(); changed?.();
      return;
    }
    if (!s.running) return;
    const ps = packetsFor(ev, rt, dev, s);
    if (!ps.length) return;
    for (const p of ps) { p.no = s.packets.length + 1; p.time = (s.packets.length * 0.0213).toFixed(6); s.packets.push(p); }
    if (s.packets.length > 300) s.packets.splice(0, s.packets.length - 300);
    if (s.packets.length && !rt.widget(id).done) (done || (() => rt.widgetDone(id)))();
    rt.collectEvidence({ type: 'capture', stage: rt.stage?.id }); // capture-based evidence rules
    rt.save();
    rt.emit('widget', id);
    changed?.();
  });
}

/** Capture widget. `dev` = host whose NIC is captured. */
export function captureWidget({ id, dev = 'PC1', title }) {
  return {
    id, blocker: 'Capture traffic on PC1',
    // completes itself as soon as packets are captured (the server replays the capture too)
    selfCompleting: true,
    headless: (rt) => attachCapture(rt, id, dev),
    mount(el, rt, api) {
      const s = api.state.data;
      s.packets = s.packets || [];
      let sel = null;
      subs.get(id)?.();
      subs.set(id, attachCapture(rt, id, dev, { done: () => api.done(), changed: () => render() }));
      const render = () => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title || `PACKET CAPTURE · ${dev} · Ethernet`, h('span', { class: s.running ? 'ok' : '' }, s.running ? '● CAPTURING' : '○ STOPPED')));
        el.append(h('div', { class: 'row' },
          h('button', { class: 'btn small', 'data-testid': `cap-toggle-${id}`, onclick: () => { rt.action(s.running ? 'capture-stop' : 'capture-start', { widget: id }); render(); } }, s.running ? '■ Stop capture' : '▶ Start capture'),
          h('button', { class: 'btn small ghost', onclick: () => { sel = null; rt.action('capture-clear', { widget: id }); render(); } }, 'Clear'),
          h('span', { class: 'muted small' }, s.running ? `Now run commands on ${dev}. Click a packet to inspect it.` : 'Start the capture, then generate traffic.')));
        const cap = h('div', { class: 'capture', role: 'table' },
          h('div', { class: 'cap-row head' }, h('span', {}, 'No.'), h('span', {}, 'Time'), h('span', {}, 'Source'), h('span', {}, 'Destination'), h('span', {}, 'Proto'), h('span', {}, 'Info')));
        for (const p of s.packets.slice(-60)) {
          cap.append(h('div', { class: `cap-row ${p.l4 === 'TCP' ? 'tcp' : p.l4 === 'UDP' ? 'udp' : ''} ${sel === p.no ? 'sel' : ''}`, 'data-no': p.no, onclick: () => { sel = p.no; rt.action('inspect-packet', { widget: id, no: p.no }); render(); } },
            h('span', {}, p.no), h('span', {}, p.time), h('span', {}, p.src), h('span', {}, p.dst), h('span', {}, p.proto), h('span', {}, p.info)));
        }
        if (!s.packets.length) cap.append(h('div', { class: 'pad muted small' }, 'No packets captured.'));
        const selected = s.packets.find((p) => p.no === sel);
        if (selected) cap.append(h('div', { class: 'cap-detail' }, detail(selected)));
        el.append(cap);
      };
      render();
    },
  };
}
export const capturedPackets = (rt, id) => rt.widget(id).data.packets || [];
